import { Button } from '../../../typography/button/button';
import { FormProps, scopeToProductVariant } from '../types';
import { SliderInput } from '@/isomorphic/components/forms/components/slider-input';
import { FormSlider } from '@/isomorphic/components/forms/components/form-slider';
import { Results } from './results';
import { Field } from '@/isomorphic/components/forms/components/field';
import { validators } from '@/hooks/use-validation';
import { useLocalization } from '@/hooks/use-localization';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '@/contexts/app-context';
import { Arrow } from './svg';
import { ConsumerApplicationProduct, useDataLayer } from '@/hooks/use-data-layer';

export const Form = ({
    calculatorState,
    loanAmountMin,
    loanAmountMax,
    loanAmountStep,
    tenorYearsMin,
    tenorYearsMax,
    monthlyAmount,
    formatNumber,
    updateState,
    getLabel,
    login,
    fields,
    updateFields,
    setStep,
    brandColor,
    scope = 'carla_se',
    skipBankIdLogin = false,
}: FormProps) => {
    const { user } = useContext(AppContext);
    const { getLanguage, localize } = useLocalization();
    const { dataLayerPush } = useDataLayer();

    const getRangeDescription = (min: number, max: number) => {
        return localize('input-range-description').replace('{min}', formatNumber(min)).replace('{max}', formatNumber(max));
    };
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [showGovIdError, setShowGovIdError] = useState(false);

    const validateSsn = (value: string) => validators.socialSecurityNumber(value, getLanguage());
    const govIdValid = useMemo(() => validateSsn(fields.governmentId), [fields.governmentId]);

    useEffect(() => {
        if (calculatorState.showGovIdField) {
            document.getElementById('government-id')?.focus();
        }
    }, [calculatorState.showGovIdField]);

    const update = (value: number | string | null, key: 'loanAmount' | 'tenorYears' = 'loanAmount') => {
        updateState(key, Number(value));
    };

    const pushLoanCalculatorEvent = () => {
        dataLayerPush({
            event: 'LoanCalculator',
            ProductVariant: scopeToProductVariant[scope],
            Product: ConsumerApplicationProduct['BRANDED_CONSUMER_LOAN'],
            LoanValue: calculatorState.loanAmount,
            PaybackPeriod: calculatorState.tenorYears,
        });
    };

    return (
        <div className="r-flex r-flex-col r-items-center r-justify-center r-gap-6 md:r-flex-row md:r-gap-10">
            <div className="r-hidden xl:r-block xl:r-w-44" />
            <div className="r-relative r-w-full r-space-y-8 r-pb-6 md:r-max-w-xl md:r-px-4">
                <fieldset className="r-m-0 r-flex r-flex-col r-border-none r-p-0">
                    <legend className="r-sr-only">
                        {getLabel('loanAmountTitle')} - {localize('input-slider-group-description')}
                    </legend>
                    <SliderInput
                        label={getLabel('loanAmountTitle')}
                        suffix={getLabel('currency')}
                        min={loanAmountMin}
                        max={loanAmountMax}
                        value={calculatorState.loanAmount}
                        onChange={value => update(value)}
                        formatNumber={formatNumber}
                        defaultStep={loanAmountStep}
                        rangeDescription={getRangeDescription(loanAmountMin, loanAmountMax)}
                    />
                    <FormSlider
                        step={loanAmountStep || 1000}
                        min={loanAmountMin}
                        max={loanAmountMax}
                        value={calculatorState.loanAmount}
                        trackColor={brandColor}
                        onChange={value => update(value)}
                        label={getLabel('loanAmountTitle')}
                        suffix={getLabel('currency')}
                        formatNumber={formatNumber}
                    />
                    <div className="r-my-6 r-flex r-w-full r-justify-between" aria-hidden="true">
                        <span>
                            {formatNumber(loanAmountMin)} {getLabel('currency')}
                        </span>
                        <span>
                            {formatNumber(loanAmountMax)} {getLabel('currency')}
                        </span>
                    </div>
                </fieldset>
                <fieldset className="r-m-0 r-flex r-flex-col r-border-none r-p-0">
                    <legend className="r-sr-only">
                        {getLabel('tenorTitle')} - {localize('input-slider-group-description')}
                    </legend>
                    <SliderInput
                        label={getLabel('tenorTitle')}
                        suffix={
                            typeof calculatorState.tenorYears === 'number' && calculatorState.tenorYears > 1 ? getLabel('years') : getLabel('year')
                        }
                        min={tenorYearsMin}
                        max={tenorYearsMax}
                        value={calculatorState.tenorYears}
                        onChange={value => update(value, 'tenorYears')}
                        formatNumber={formatNumber}
                        rangeDescription={getRangeDescription(tenorYearsMin, tenorYearsMax)}
                    />
                    <FormSlider
                        min={tenorYearsMin}
                        max={tenorYearsMax}
                        value={calculatorState.tenorYears}
                        trackColor={brandColor}
                        onChange={value => update(value, 'tenorYears')}
                        label={getLabel('tenorTitle')}
                        suffix={calculatorState.tenorYears === 1 ? getLabel('year') : getLabel('years')}
                        formatNumber={formatNumber}
                    />
                    <div className="r-mt-6 r-flex r-w-full r-justify-between" aria-hidden="true">
                        <span>
                            {tenorYearsMin + ' '}
                            {typeof calculatorState.tenorYears === 'number' && calculatorState.tenorYears > 1 ? getLabel('years') : getLabel('year')}
                        </span>
                        <span>
                            {tenorYearsMax + ' '}
                            {typeof calculatorState.tenorYears === 'number' && calculatorState.tenorYears > 1 ? getLabel('years') : getLabel('year')}
                        </span>
                    </div>
                </fieldset>

                <div className="xl:r-hidden">
                    <Results monthlyAmount={monthlyAmount} monthlyAmountTitle={getLabel('monthlyAmountTitle')} brandColor={brandColor} />
                </div>

                {user?.isAuthenticated && fields.governmentId ? (
                    <div className="r-flex r-flex-col">
                        <div className="r-flex r-justify-end">
                            <Button
                                onClick={() => {
                                    pushLoanCalculatorEvent();
                                    setStep(1);
                                }}
                            >
                                <span className="r-flex r-gap-2 r-px-6">
                                    <span>{getLabel('continueButton')}</span>
                                    <Arrow className="r-w-4" aria-hidden="true" />
                                </span>
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        {calculatorState.showGovIdField ? (
                            <div>
                                <Field
                                    fieldName="government-id"
                                    value={fields.governmentId}
                                    validation={value => validateSsn(value)}
                                    label={getLabel('governmentIdPlaceholder')}
                                    errorMessage={getLabel('governmentIdValidationMessage')}
                                    showRequiredError={showGovIdError}
                                    onChange={value => {
                                        updateFields({ governmentId: value });
                                        if (showGovIdError) setShowGovIdError(false);
                                    }}
                                />
                                <p className="r-pt-2 r-text-xs">{getLabel('noCreditCheck')}</p>
                            </div>
                        ) : null}
                        <div className="r-flex r-flex-col">
                            <div className="r-flex r-justify-end [&>button]:r-mr-0">
                                {calculatorState.showGovIdField ? (
                                    <Button
                                        variant={skipBankIdLogin ? 'standard' : 'bankidwhite'}
                                        brandColor={brandColor}
                                        className="r-px-6"
                                        disabled={isLoggingIn}
                                        onClick={() => {
                                            if (!govIdValid) {
                                                setShowGovIdError(true);
                                                document.getElementById('government-id')?.focus();
                                                return;
                                            }
                                            pushLoanCalculatorEvent();
                                            setIsLoggingIn(true);
                                            login(fields.governmentId);
                                        }}
                                    >
                                        {getLabel('applyButton')}
                                    </Button>
                                ) : (
                                    <Button
                                        brandColor={brandColor}
                                        className="r-px-6"
                                        onClick={() => updateState('showGovIdField', !calculatorState.showGovIdField)}
                                    >
                                        {getLabel('submitButton')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <div className="r-hidden xl:r-flex">
                <Results monthlyAmount={monthlyAmount} monthlyAmountTitle={getLabel('monthlyAmountTitle')} brandColor={brandColor} />
            </div>
        </div>
    );
};
